import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';
import { paymentRepository } from '../repositories/payment.repository';
import { paymentKeys } from '../services/payments.keys';
import type { AddPaymentData } from '../types/payment.types';

export type { AddPaymentData };

export const useAddPayment = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (paymentData: AddPaymentData) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }
      return paymentRepository.addPayment(currentOrganization.id, paymentData);
    },
    onSuccess: (data) => {
      const campaignId = data?.campaign_influencers?.campaigns?.id;

      if (campaignId) {
        queryClient.invalidateQueries({ queryKey: paymentKeys.campaignPayments(campaignId) });
        queryClient.invalidateQueries({ queryKey: ['campaign-budget-calculations', campaignId] });
        queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
        queryClient.invalidateQueries({ queryKey: ['campaign-with-contact', campaignId] });
      }

      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      queryClient.invalidateQueries({ queryKey: ['campaign-payments'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-payments'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-budget-calculations'] });

      toast({
        title: 'Success',
        description: 'Payment added successfully',
      });
    },
    onError: (error: Error) => {
      console.error('Add payment error:', error);
      toast({
        title: 'Error',
        description: 'Failed to add payment. Please try again.',
        variant: 'destructive',
      });
    },
  });
};
